import {
  Component,
  OnInit,
  OnDestroy,
  Output,
  EventEmitter,
} from '@angular/core';
import { Subscription } from 'rxjs';

import { ChatbotStateService } from 'src/app/_services/chatbot/chatbot-state.service';
import { ChatbotApiService } from 'src/app/_services/chatbot/chatbot-api.service';
import { ChatbotSession } from 'src/app/interfaces/chatbot-session.interfaces';

/**
 * SessionListComponent — Lista de sesiones de conversación anteriores.
 *
 * Muestra las sesiones del usuario autenticado, permite seleccionar una
 * sesión existente para cargar su historial, y permite crear una nueva
 * sesión de conversación.
 *
 * El componente se suscribe al estado centralizado (ChatbotStateService)
 * para reflejar cambios reactivos en la lista de sesiones y la sesión activa.
 * La carga inicial de sesiones se delega al componente padre (ChatbotPanelComponent)
 * para evitar llamadas duplicadas; este componente solo consume el estado.
 *
 * Requisitos: 4.2, 4.3, 5.3, 5.7, 5.8
 */
@Component({
  selector: 'app-session-list',
  templateUrl: './session-list.component.html',
  styleUrls: ['./session-list.component.css'],
})
export class SessionListComponent implements OnInit, OnDestroy {

  // ---------------------------------------------------------------------------
  // Eventos de salida
  // ---------------------------------------------------------------------------

  /**
   * Emitido cuando el usuario selecciona una sesión existente.
   * El componente padre es responsable de cargar los mensajes de la sesión.
   */
  @Output() sessionSelected = new EventEmitter<ChatbotSession>();

  /**
   * Emitido cuando el usuario solicita crear una nueva sesión.
   */
  @Output() newSessionRequested = new EventEmitter<void>();

  // ---------------------------------------------------------------------------
  // Estado del componente
  // ---------------------------------------------------------------------------

  /** Lista de sesiones del usuario. */
  sessions: ChatbotSession[] = [];

  /** Sesión actualmente seleccionada. */
  activeSession: ChatbotSession | null = null;

  /** Indica si las sesiones se están cargando desde el backend. */
  isLoadingSessions = false;

  // ---------------------------------------------------------------------------
  // Suscripciones
  // ---------------------------------------------------------------------------

  private subscriptions = new Subscription();

  // ---------------------------------------------------------------------------
  // Constructor e inyección de dependencias
  // ---------------------------------------------------------------------------

  constructor(
    private stateService: ChatbotStateService,
    private apiService: ChatbotApiService,
  ) {}

  // ---------------------------------------------------------------------------
  // Ciclo de vida
  // ---------------------------------------------------------------------------

  ngOnInit(): void {
    this._subscribeToState();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  // ---------------------------------------------------------------------------
  // Acciones del usuario
  // ---------------------------------------------------------------------------

  /**
   * Selecciona una sesión existente y notifica al componente padre.
   * No realiza ninguna acción si la sesión ya está activa.
   *
   * Req 4.3: al seleccionar una sesión se carga el historial completo.
   *
   * @param session  Sesión a activar.
   */
  selectSession(session: ChatbotSession): void {
    if (this.activeSession?.session_id === session.session_id) {
      return;
    }
    this.sessionSelected.emit(session);
  }

  /**
   * Solicita la creación de una nueva sesión de conversación.
   * Limpia la sesión activa en el estado centralizado y notifica al padre.
   */
  createNewSession(): void {
    this.stateService.setActiveSession(null);
    this.stateService.setMessages([]);
    this.newSessionRequested.emit();
  }

  // ---------------------------------------------------------------------------
  // Helpers de plantilla
  // ---------------------------------------------------------------------------

  /**
   * Formatea la fecha de última actualización de una sesión para mostrarla
   * en la lista. Devuelve una cadena vacía si la fecha no es válida.
   *
   * @param isoDate  Fecha en formato ISO 8601.
   */
  formatSessionDate(isoDate: string): string {
    if (!isoDate) {
      return '';
    }
    try {
      const date = new Date(isoDate);
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

      if (diffDays === 0) {
        return date.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });
      } else if (diffDays === 1) {
        return 'Ayer';
      } else if (diffDays < 7) {
        return date.toLocaleDateString('es-MX', { weekday: 'short' });
      } else {
        return date.toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit', year: '2-digit' });
      }
    } catch {
      return '';
    }
  }

  /**
   * Devuelve el título de la sesión o un texto por defecto si está vacío.
   *
   * @param session  Sesión cuyo título se quiere mostrar.
   */
  getSessionTitle(session: ChatbotSession): string {
    return session.title?.trim() || 'Conversación';
  }

  /**
   * Función de tracking para *ngFor que evita re-renderizados innecesarios
   * al identificar cada sesión por su ID único.
   *
   * @param _index   Índice del elemento en el arreglo.
   * @param session  Sesión a identificar.
   */
  trackBySessionId(_index: number, session: ChatbotSession): string {
    return session.session_id;
  }

  // ---------------------------------------------------------------------------
  // Métodos privados
  // ---------------------------------------------------------------------------

  /**
   * Suscribe el componente a los observables del estado centralizado para
   * reflejar cambios reactivos en la lista de sesiones y la sesión activa.
   */
  private _subscribeToState(): void {
    this.subscriptions.add(
      this.stateService.sessions$.subscribe((sessions) => {
        this.sessions = sessions;
      })
    );

    this.subscriptions.add(
      this.stateService.activeSession$.subscribe((session) => {
        this.activeSession = session;
      })
    );
  }
}
